// See Tettra Permissions: https://docs.google.com/spreadsheets/d/1H_g6tyn9wIu-I3W7Q5U0YQdkkYZMbvESPvTvLh5YAN0
// window.userRole set in app.blade
import { PAGE_VISIBILITY, USER_ROLE } from './CONSTANTS';
import {
    hasKaiFeature,
} from './featureHelper';

/*
 * Generic role helpers
 */
const isAdmin = () => {
    const { userRole } = window;

    if (!window.loggedIn || typeof userRole === 'undefined') return false;

    return userRole === USER_ROLE.ADMIN;
};

const isPagePubliclyVisible = () => {
    const { page } = window;

    return (page && (page.visibility === PAGE_VISIBILITY.PUBLICLY_VISIBLE));
};

const isAdminOrEditor = () => {
    const { userRole } = window;

    if (!window.loggedIn || typeof userRole === 'undefined') return false;

    return [USER_ROLE.ADMIN, USER_ROLE.EDITOR].includes(userRole);
};

const isAdminOrEditorOrContributor = () => {
    const { userRole } = window;
    if (!window.loggedIn || typeof userRole === 'undefined') return false;

    return [USER_ROLE.ADMIN, USER_ROLE.EDITOR, USER_ROLE.CONTRIBUTOR].includes(userRole);
};

const isGuest = () => {
    const { userRole } = window;
    if (!window.loggedIn || typeof userRole === 'undefined') return false;

    return userRole === USER_ROLE.GUEST;
};

const isReadOnly = () => {
    const { userRole } = window;
    if (!window.loggedIn || typeof userRole === 'undefined') return false;

    return userRole === USER_ROLE.READ_ONLY;
};

const hasScimSync = () => window.hasActiveScimConnectionAndSyncEnabled ?? false;

/* Admin permissions */

const canCreateDashboardPin = isAdmin;

const canDeleteComment = isAdmin;

const canUpdateLockedPage = isAdmin;

const canEditUserRole = isAdmin;

const canVerifyPage = isAdmin;

const canVerifyDocument = isAdmin;

const canSetRecentActivitySettings = isAdmin;

const canBulkReassignPageOwner = isAdminOrEditor;

/* Admin or editor permissions */

const canEditCategory = isAdminOrEditor;

const canCreateCategory = isAdminOrEditor;

const canCreateSubcategory = isAdminOrEditor;

const canViewSubcategoryActions = isAdminOrEditorOrContributor;

const canUpdateTeam = isAdminOrEditor;

const canCreateVerificationSchedule = isAdminOrEditorOrContributor;

const canDeleteVerificationSchedule = isAdminOrEditorOrContributor;

const canDeleteScheduleRequest = isAdmin;

const canUpdateVerificationSchedule = isAdminOrEditorOrContributor;

const canInviteUsers = isAdminOrEditor;

const canEditGuestUserCategories = isAdminOrEditor;

const canEditSuggestion = isAdminOrEditor;

const canCreateLink = isAdminOrEditorOrContributor;

const canCreateExternalSites = isAdmin;

const canEditTemplates = isAdminOrEditorOrContributor;

const canViewArchived = isAdminOrEditorOrContributor;

/* Any authenticated user, regardless of role */

const canViewTeam = () => {
    const { userRole } = window;

    return window.loggedIn && typeof userRole !== 'undefined';
};

const canViewKaiDashboard = () => {
    const { userRole } = window;
    if (!window.loggedIn || typeof userRole === 'undefined') return false;

    return [USER_ROLE.ADMIN, USER_ROLE.EDITOR].includes(userRole);
};

const canViewVerificationSchedule = canViewTeam;

// this does not handle guest users correctly because it requires window.categoriesAndFolders
// which is loaded async and not guaranteed to be defined yet
// thus guest users are shown more restricted options than they actually have access to
const canCreatePage = () => {
    const { userRole } = window;
    if (!window.loggedIn || typeof userRole === 'undefined') return false;

    return [USER_ROLE.ADMIN, USER_ROLE.EDITOR, USER_ROLE.CONTRIBUTOR].includes(userRole);
};

const canBulkMove = () => {
    const { userRole } = window;
    if (!window.loggedIn || typeof userRole === 'undefined') return false;

    return [USER_ROLE.ADMIN, USER_ROLE.EDITOR, USER_ROLE.CONTRIBUTOR].includes(userRole);
};

/* Questions permissions */

const canCreateQuestion = () => canCreatePage();

const canDeleteQuestion = (question) => isAdmin() || question.asker_id === window.userId;

const canEditQuestion = (question) => isAdminOrEditorOrContributor() || question.asker_id === window.userId;

const canMarkAnswer = () => window.loggedIn && window.userRole !== USER_ROLE.READ_ONLY;

const canUnmarkAnswer = (answer) => isAdminOrEditorOrContributor() || window.userId === answer.user_id;

const canEditAnswer = (answer) => isAdmin() || window.userId === answer.comment.user_id;

const canDeleteGeneratedQuestion = () => isAdmin();

const canEditGeneratedAnswer = () => isAdminOrEditorOrContributor();

/* Other */

const canViewRevision = () => {
    const { userRole } = window;
    if (!window.loggedIn || typeof userRole === 'undefined') return false;

    return [USER_ROLE.ADMIN, USER_ROLE.EDITOR, USER_ROLE.CONTRIBUTOR].includes(userRole);
};

const canSuggestEdit = isReadOnly;

const canViewVerifiedPages = isAdminOrEditorOrContributor;

const canViewStalePages = isAdminOrEditorOrContributor;

const canViewUnownedPages = isAdminOrEditor;

const canViewPublicContent = isAdmin;

const canEditTeamHome = isAdmin;

const canShowVerification = !(isPagePubliclyVisible && !window.loggedIn);

const canCreateSharableLink = !isReadOnly();

const canViewAiGeneratedQuestions = () => (hasKaiFeature() && !isGuest());

const canViewKaiSlackQuestions = () => hasKaiFeature() && isAdmin();

const canViewSetupGuide = () => {
    const hasSetupGuide = !!document.querySelector('#main-sidebar-container').dataset.showSetupGuide;
    return isAdminOrEditorOrContributor() && hasSetupGuide;
};

export {
    canBulkMove,
    canCreateQuestion,
    canCreatePage,
    canSuggestEdit,
    canCreateCategory,
    canCreateSubcategory,
    canEditCategory,
    canViewRevision,
    canUpdateTeam,
    canViewTeam,
    canViewKaiDashboard,
    canCreateDashboardPin,
    canDeleteComment,
    canUpdateLockedPage,
    canCreateVerificationSchedule,
    canDeleteVerificationSchedule,
    canDeleteScheduleRequest,
    canUpdateVerificationSchedule,
    canViewVerificationSchedule,
    canInviteUsers,
    canEditGuestUserCategories,
    canEditUserRole,
    canVerifyPage,
    canVerifyDocument,
    canDeleteQuestion,
    canEditQuestion,
    canMarkAnswer,
    canUnmarkAnswer,
    canEditAnswer,
    canDeleteGeneratedQuestion,
    canEditGeneratedAnswer,
    canViewVerifiedPages,
    canViewStalePages,
    canViewUnownedPages,
    canViewPublicContent,
    canEditSuggestion,
    canCreateLink,
    canCreateExternalSites,
    canEditTemplates,
    canViewArchived,
    canEditTeamHome,
    canShowVerification,
    canViewSubcategoryActions,
    canCreateSharableLink,
    canViewAiGeneratedQuestions,
    canViewKaiSlackQuestions,
    hasScimSync,
    canSetRecentActivitySettings,
    canBulkReassignPageOwner,
    canViewSetupGuide,
};
